// @flow

import pickBy from 'lodash/pickBy';
import * as urls from '../urls';
import * as request from 'app/utils/request';
import {gt, ngt} from 'app/utils/i18n';
import {limitRequests} from 'app/utils/redux';

import {
  showNotification,
  showUpgradeModal,
  UPGRADE_MODALS,
} from 'app/toasts/slice';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import type {ResponseType} from 'app/utils/redux_v2';
import type {
  UsersFetchParamsType,
  UsersLocationParamsType,
} from 'app/user/types';
import type {NewUserType, UserType, NewInvitationType} from 'app/user/types';
import {taskDispatcher} from 'app/subscriptions/taskDispatcher';
import type {StoreShapeType} from 'app/reducers/types';
import {showNonFieldErrorMessageHandler} from 'app/utils/request';

export const RECEIVE_DELETE_INVITATION = 'RECEIVE_DELETE_INVITATION';
export const RECEIVE_USER_DETAILS = 'RECEIVE_USER_DETAILS';

export type UsersActions = ReceiveActivityListType | ReceiveInvitationListType;

const getFetchErrorMsg = () =>
  gt('There was an error fetching the invitations');

/* user list */

export const receiveUserDetails = (data: UserType) => ({
  type: RECEIVE_USER_DETAILS,
  data,
});

export const convertLocationParamsToFetchParams = (
  query: UsersLocationParamsType
): UsersFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  search: query.search ? query.search : undefined,
  ordering: query.ordering || 'first_name,last_name',
  team_id: query.team_id,
  exclude_team_id: query.exclude_team_id,
  include_shadow_user: query.include_shadow_user,
  id: query.id != null ? query.id : undefined,
  exclude_id: query.exclude_id,
  status: query.status,
  account_role: query.account_role,
  in_no_team_roles: query.in_no_team_roles,
  exclude_role: query.exclude_role,
  exclude_guests: query.exclude_guests,
});

const requestUserList = (params: any) =>
  request.get(urls.USER_API_URL, {
    ...params,
    omit: 'num_accounts,notifications_modified_at',
  });

export const requestUserListPermissionByDocument = (
  objectId: string | number,
  contentType: 'procedure' | 'proceduregroup' | 'policy',
  args: {|
    with_permissions?: number,
    search?: string,
    page_size?: number,
  |} = Object.freeze({})
) =>
  request.get(`${urls.USER_API_URL}permissions`, {
    object_id: objectId,
    content_type: contentType,
    page_size: 10,
    ...args,
  });

export const RECEIVE_USER_LIST = 'RECEIVE_USER_LIST';

type ReceiveUserListType = {
  type: 'RECEIVE_USER_LIST',
  query: UsersFetchParamsType,
  response: ResponseType<UserType>,
};

export const receiveUserList = (
  query: UsersFetchParamsType,
  response: ResponseType<UserType>
): ReceiveUserListType => ({
  type: RECEIVE_USER_LIST,
  query,
  response,
});

export const fetchUserList = limitRequests(
  (locationParams: UsersLocationParamsType) => (dispatch: SimpleDispatch) => {
    const fetchParams = convertLocationParamsToFetchParams(locationParams);
    return requestUserList(fetchParams).then(
      (response) => dispatch(receiveUserList(fetchParams, response)),
      request.createErrorHandler(getFetchErrorMsg(), dispatch)
    );
  }
);

// fetch user activity list

export const RECEIVE_ACTIVITY_LIST = 'RECEIVE_ACTIVITY_LIST';

type ReceiveActivityListType = {
  type: 'RECEIVE_ACTIVITY_LIST',
  query: any,
  response: any,
};

export const receiveActivityList = (
  query: any,
  response: any
): ReceiveActivityListType => ({
  type: RECEIVE_ACTIVITY_LIST,
  query,
  response,
});

type ActivityParamsType = {
  user_id: number,
  page?: number,
  page_size?: number,
};

type ActivityQueryParamsType = {
  actor: string,
  page: number,
  page_size: number,
};

export function convertActivitiesParamsToQuery({
  user_id,
  page = 1,
  page_size = DEFAULT_PAGE_SIZE,
}: ActivityParamsType): ActivityQueryParamsType {
  return {actor: user_id.toString(), page, page_size};
}

export const rawFetchActivities =
  (fetchParams: ActivityQueryParamsType) => (dispatch: SimpleDispatch) => {
    return request
      .get(urls.ACTIVITY_API_URL, fetchParams)
      .then((response) => dispatch(receiveActivityList(fetchParams, response)))
      .catch(
        request.createErrorHandler(
          gt('There was an error fetching the activities'),
          dispatch
        )
      );
  };

export const fetchActivities =
  (params: ActivityParamsType) => (dispatch: SimpleDispatch) => {
    const query = convertActivitiesParamsToQuery(params);
    return dispatch(rawFetchActivities(query));
  };

/* user invitations */

export const RECEIVE_INVITATION_LIST = 'RECEIVE_INVITATION_LIST';

type ReceiveInvitationListType = {
  type: 'RECEIVE_INVITATION_LIST',
  query: any,
  response: any,
};

type InvitationQueryParamsType = {
  user_id: string,
  page: number,
  page_size: number,
};

export const receiveInvitationList = (
  query: any,
  response: any
): ReceiveInvitationListType => ({
  type: RECEIVE_INVITATION_LIST,
  query,
  response,
});

export function convertInvitationsParamsToQuery({
  user_id,
  page = 1,
  page_size = DEFAULT_PAGE_SIZE,
}: ActivityParamsType): InvitationQueryParamsType {
  return {user_id: user_id.toString(), page, page_size};
}

export const fetchInvitations =
  (params: ActivityParamsType) => (dispatch: SimpleDispatch) => {
    const query = convertInvitationsParamsToQuery(params);

    return request
      .get(urls.INVITATION_API_URL, query)
      .then((response) => dispatch(receiveInvitationList(query, response)))
      .catch(request.createErrorHandler(getFetchErrorMsg(), dispatch));
  };

export const resendInvitation =
  (userid: number, message: string | null = null) =>
  (dispatch: FuncDispatch) =>
    request
      .postJson(
        urls.INVITATION_API_RESEND_URL,
        pickBy({send_mail: true, to_user_id: userid, message})
      )
      .then((invitation) => {
        dispatch(showNotification(gt('The invitation was re-sent.')));
        return invitation;
      })
      .catch(
        request.createErrorHandler(
          gt('Unable to re-send invitation.'),
          dispatch,
          {
            '403': (error) =>
              dispatch(showNotification(error.data.detail, 'error')),
          }
        )
      );

export const deleteInvitation = (url: string) => (dispatch: SimpleDispatch) =>
  request
    .del(url)
    .then(() => {
      return dispatch(showNotification(gt('The invitation was revoked.')));
    })
    .catch(
      request.createErrorHandler(gt('Unable to revoke invitation.'), dispatch, {
        '403': (error) =>
          dispatch(showNotification(error.data.detail, 'error')),
      })
    );

/* fetch user detail */

export const fetchUserDetails =
  (userId: number | string, userNotFoundAction?: () => void) =>
  (dispatch: SimpleDispatch) => {
    return request
      .get(`${urls.USER_API_URL}${userId}/`)
      .then((data) => dispatch(receiveUserDetails(data)))
      .catch(
        request.createErrorHandler(getFetchErrorMsg(), dispatch, {
          '404': () => {
            userNotFoundAction !== undefined && userNotFoundAction();
            dispatch(
              showNotification(gt('User not found in the account'), 'error')
            );
          },
        })
      );
  };

export const makeSureUserDataIsFetched =
  (userId: number) =>
  (dispatch: FuncDispatch, getState: () => StoreShapeType) => {
    // $FlowFixMe - byId is a Map so flow doesn't know its value
    const user = getState().userArea.getIn([
      'usersv2',
      'byId',
      userId.toString(),
    ]);

    if (user == null) {
      dispatch(fetchUserDetails(userId));
    }
  };

/* create user */

export const createUser =
  (userData: NewUserType) =>
  (dispatch: SimpleDispatch): Promise<UserType> =>
    request
      .postJson(urls.USER_API_URL, userData)
      .then((data) => {
        dispatch(receiveUserDetails(data));
        return data;
      })
      .catch(
        request.createErrorHandler(
          gt('A problem on our server occurred. Unable to add member'),
          dispatch,
          {
            '403': (error) => {
              if (error.data.detail === urls.STATUSES.UPGRADE_REQUIRED) {
                dispatch(showUpgradeModal(UPGRADE_MODALS.USER_LIMIT));
                throw {error: error.data.detail};
              } else {
                dispatch(showNotification(error.data.detail, 'error'));
              }
              throw error;
            },
          }
        )
      );

export const createUserNoErrorHandling =
  (userData: NewUserType) =>
  (dispatch: SimpleDispatch): Promise<UserType> =>
    request.postJson(urls.USER_API_URL, userData).then((data) => {
      dispatch(receiveUserDetails(data));
      return data;
    });

export const patchUser =
  (user: UserType, data: $Shape<UserType>) => (dispatch: SimpleDispatch) =>
    request
      .patchJson(user.url, data)
      .then((data) => {
        dispatch(receiveUserDetails(data));
        return dispatch(showNotification(gt('Member details updated.')));
      })
      .catch(
        request.createErrorHandler(
          gt(
            'A problem on our server occurred. Unable to update member details.'
          ),
          dispatch,
          {
            '403': (error) => {
              if (error.data.detail === urls.STATUSES.UPGRADE_REQUIRED) {
                dispatch(showUpgradeModal(UPGRADE_MODALS.USER_LIMIT));
                return {error: error.data.detail};
              } else {
                dispatch(showNotification(error.data.detail, 'error'));
              }
              return error;
            },
            ...showNonFieldErrorMessageHandler(
              true,
              gt('Could not update member')
            ),
          }
        )
      );

export const removeUser = (url: string) => (dispatch: SimpleDispatch) =>
  request
    .del(url)
    .then(() => {
      dispatch(showNotification(gt('Removed member')));
    })
    .catch(
      request.createErrorHandler(
        gt('A problem on our server occurred. Unable to remove member'),
        dispatch,
        {
          '403': (error) => {
            return dispatch(showNotification(error.data.detail, 'error'));
          },
        }
      )
    );

export const asyncCreateInvitations =
  (invitations: Array<NewInvitationType>) => (dispatch: SimpleDispatch) => {
    const extraTimeMessage =
      invitations.length >= 200
        ? ` ${gt('It may take some minutes.')}`
        : invitations.length >= 50
        ? ` ${gt('It may take some seconds.')}`
        : '';

    const taskPromise = request
      .postJson(urls.INVITATION_API_DELAY_URL, invitations)
      .then((data) => {
        dispatch(
          showNotification(
            `${ngt(
              'Sending invitation...',
              'Sending invitations...',
              invitations.length
            )}${extraTimeMessage}`
          )
        );
        return data;
      });

    return ((dispatch: any): FuncDispatch)(taskDispatcher(taskPromise)).then(
      (data) => {
        dispatch(
          showNotification(
            ngt('Invitation sent.', 'Invitations sent.', invitations.length)
          )
        );
        return data;
      },
      () =>
        dispatch(
          showNotification(
            ngt(
              'Unable to send invitation to user.',
              'Unable to send those invitations.',
              invitations.length
            )
          )
        )
    );
  };

export const dynamicCreateInvitations =
  (invitations: Array<NewInvitationType>, syncThreshold: number = 5) =>
  (dispatch: FuncDispatch) =>
    dispatch(
      (invitations.length > syncThreshold
        ? asyncCreateInvitations
        : createInvitations)(invitations)
    );

export const createInvitations =
  (invitations: Array<NewInvitationType>) => (dispatch: SimpleDispatch) =>
    request
      .postJson(urls.INVITATION_API_URL, invitations)
      .then(() => {
        return dispatch(
          showNotification(
            ngt('Invitation sent.', 'Invitations sent.', invitations.length)
          )
        );
      })
      .catch(
        request.createErrorHandler(
          ngt(
            'Unable to send invitation to user.',
            'Unable to send those invitations.',
            invitations.length
          ),
          dispatch,
          {
            '403': (error) =>
              dispatch(showNotification(error.data.detail, 'error')),
            ...showNonFieldErrorMessageHandler(
              true,
              gt('Could not send invitation')
            ),
          }
        )
      );
